/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Known partner tier strings as they appear in Airtable. Open union — keeps
 * `string` as the fallback so unexpected values flow through `classifyTier`
 * rather than tripping a type error at the fetcher boundary.
 */
export type PartnerBadgeLevel = "Bronze" | "Silver" | "Gold" | "Academic" | (string & {});

/**
 * Normalized partner tier. Derived from `badgeLevel` via `classifyTier` so
 * cards never need to string-sniff badge labels themselves.
 */
export type PartnerTier = "bronze" | "silver" | "gold" | "no-badge" | "academic" | "unknown";

/**
 * Classify a raw Airtable `badgeLevel` string into one of the known tiers.
 * Case-insensitive, trims whitespace; anything containing "academic" maps to
 * the academic tier so multi-word labels ("Academic Partner") still resolve.
 */
export function classifyTier(badgeLevel: string | undefined): PartnerTier {
	const v = badgeLevel?.toLowerCase().trim() ?? "";
	if (v.includes("academic")) return "academic";
	if (v === "bronze") return "bronze";
	if (v === "silver") return "silver";
	if (v === "gold") return "gold";
	if (v === "no-badge") return "no-badge";
	return "unknown";
}

/**
 * A Gold-certified instructor from the UAI Airtable base.
 *
 * Source: `appTXNGNPznnzwjC0` / `MASTER copy 3`, filtered to
 * `{2026 Badge}='GOLD'`.
 */
export interface Instructor {
	/**
	 * Stable identifier — Airtable's numeric `ID` field when present, falling
	 * back to the record's `recXXX` row id otherwise. String type accommodates
	 * both so React `key={card.id}` stays stable.
	 */
	id: string;
	name: string;
	country: string;
	industries: string[];
	geo: string;
	/**
	 * Raw `2026 Badge` value from Airtable. Always `"GOLD"` since the fetcher
	 * filters to that tier — typed as plain string so a future tier rollout
	 * (Silver/Bronze instructors) doesn't break the contract. Cards don't
	 * need to read it; the variant rendering is hard-coded to gold.
	 *
	 * Distinct from `PartnerBadgeLevel` (Bronze/Silver/Gold/Academic) on
	 * purpose — Instructor and Partner tier vocabularies are independent.
	 */
	badgeLevel: string;
	linkedInUrl?: string;
	edcUrl?: string;
	artStationUrl?: string;
	yearQualified?: string;
}

/**
 * A training partner from the UE training-partner Airtable base.
 *
 * Schema not yet finalized — fields here are the planned shape based on the
 * Figma design (logo, name, country, badge tier, delivery-mode tags). Wiring
 * lands when the partner page goes live; for now the fetcher returns `[]`.
 */
export interface TrainingPartner {
	id: string;
	name: string;
	country: string;
	industries: string[];
	logoUrl?: string;
	websiteUrl?: string;
	email?: string;
	/** Open union of known tiers. Use `classifyTier` to normalize to `PartnerTier`. */
	badgeLevel?: PartnerBadgeLevel;
	languages?: string[];
}

/**
 * A service partner (future consumer).
 *
 * Adds `services: string[]` for the Services dropdown filter unique to
 * Service Partner pages. Otherwise mirrors the partner card shape.
 */
export interface ServicePartner {
	id: string;
	name: string;
	country: string;
	industries: string[];
	services: string[];
	logoUrl?: string;
	websiteUrl?: string;
	email?: string;
	badgeLevel?: PartnerBadgeLevel;
	languages?: string[];
	officeLocation?: string;
}
